'use client';

import { useEffect, useRef, useState } from 'react';
import { useLoadedAvifSource } from '@/components/tools/avif-tool-shared';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  formatFileSize,
  optimizeAvif,
  type AvifOptimizationPreset,
} from '@/lib/browser-avif-tools';
import { getBaseName } from '@/lib/browser-image-tools';

export default function BrowserAvifOptimizer() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { source, loadSource, clearSource, isSupported } = useLoadedAvifSource();
  const [preset, setPreset] = useState<AvifOptimizationPreset>('balanced');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [resultPreviewUrl, setResultPreviewUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      [downloadUrl, resultPreviewUrl].forEach((url) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [downloadUrl, resultPreviewUrl]);

  function resetOutputState() {
    [downloadUrl, resultPreviewUrl].forEach((url) => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    });

    setDownloadUrl(null);
    setResultPreviewUrl(null);
    setDownloadName(null);
    setResultSize(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      clearSource();
      return;
    }

    try {
      await loadSource(file);
    } catch (error) {
      clearSource();
      setErrorMessage(
        error instanceof Error ? error.message : 'The AVIF file could not be opened.',
      );
    }
  }

  async function runOptimization() {
    if (!source) {
      setErrorMessage('Choose one AVIF first.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        'avif-compressor',
        'Credits are not available for another AVIF optimization right now.',
      );
      const result = await optimizeAvif({
        file: source.file,
        preset,
      });
      const nextDownloadUrl = URL.createObjectURL(result.blob);
      const nextPreviewUrl = URL.createObjectURL(result.previewBlob);

      resetOutputState();
      setDownloadUrl(nextDownloadUrl);
      setResultPreviewUrl(nextPreviewUrl);
      setDownloadName(`${getBaseName(source.file.name)}-optimized.avif`);
      setResultSize(result.blob.size);
      setCreditMessage(formatToolCreditSuccess('AVIF optimization', credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The AVIF file could not be optimized.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  const savings =
    source && resultSize !== null
      ? `${Math.round((1 - resultSize / Math.max(source.file.size, 1)) * 100)}%`
      : null;

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Optimize a still AVIF with focused presets</h2>
          <p>
            Shrink one AVIF locally with balanced, smaller, or aggressive presets when you want a
            dedicated AVIF workflow instead of a generic compressor.
          </p>
        </div>
        <span className="tool-live-badge">AVIF</span>
      </div>

      {!isSupported ? (
        <p className="support-feedback support-feedback-error">
          This browser cannot process AVIF locally yet. Use a modern Chromium or Safari build with
          ImageDecoder and OffscreenCanvas support.
        </p>
      ) : null}

      <div className="tool-live-routes" aria-label="AVIF optimization presets">
        {[
          ['balanced', 'Balanced'],
          ['smaller', 'Smaller file'],
          ['aggressive', 'Aggressive'],
        ].map(([id, label]) => (
          <button
            key={id}
            className={`tool-live-route ${preset === id ? 'tool-live-route-active' : ''}`}
            onClick={() => {
              setPreset(id as AvifOptimizationPreset);
              resetOutputState();
            }}
            type="button"
          >
            {label}
          </button>
        ))}
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            disabled={!isSupported}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{source ? source.file.name : 'Choose one AVIF or drop it here'}</strong>
              <span>
                {source
                  ? `${formatFileSize(source.file.size)} • ${source.metadata.width} × ${source.metadata.height}`
                  : 'Still AVIF only'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/avif,.avif"
            className="tool-live-file-input"
            disabled={!isSupported}
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isProcessing || !isSupported}
              onClick={() => void runOptimization()}
              type="button"
            >
              {isProcessing ? 'Optimizing…' : 'Optimize AVIF'}
            </button>
            {downloadUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={downloadUrl}>
                Download AVIF
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Optimization summary</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Preset</strong>
              <span>{preset}</span>
            </div>
            <div>
              <strong>Dimensions</strong>
              <span>
                {source ? `${source.metadata.width} × ${source.metadata.height}` : 'No file loaded'}
              </span>
            </div>
            <div>
              <strong>Output size</strong>
              <span>{resultSize ? formatFileSize(resultSize) : 'Not exported yet'}</span>
            </div>
            {savings ? (
              <div>
                <strong>Savings</strong>
                <span>{savings}</span>
              </div>
            ) : null}
          </div>
        </aside>
      </div>

      <div className="tool-live-preview">
        <h3>Preview</h3>
        <div className="tool-image-compare-grid">
          <div className="tool-image-compare-card">
            <strong>Source AVIF</strong>
            <div className="tool-live-preview-frame">
              {source ? (
                <img alt="Source AVIF preview" src={source.previewUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>Source preview</strong>
                  <span>Load one AVIF to optimize it.</span>
                </div>
              )}
            </div>
          </div>
          <div className="tool-image-compare-card">
            <strong>Optimized AVIF</strong>
            <div className="tool-live-preview-frame">
              {resultPreviewUrl ? (
                <img alt="Optimized AVIF preview" src={resultPreviewUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>No export yet</strong>
                  <span>Run the optimizer to preview the smaller AVIF.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
